import { useState, useEffect, useCallback } from 'react';
import { ethers } from 'ethers';
import { CONTRACTS, ABIS, ILK, ILK_WPLS, RPC_URLS } from '../utils/contracts';

// ---------------------------------------------------------------------------
// Live state for every auction venue — Clipper collateral auctions (both ilks),
// Flapper surplus auctions, Flopper debt auctions — plus the Vow's books and
// the arming state. All public view calls (FallbackProvider, no wallet); the
// optional account adds that user's bidding balances/permissions.
// ---------------------------------------------------------------------------

const RAY = 10n ** 27n;
const RAD = 10n ** 45n;
const WAD = 10n ** 18n;

const CLIP_ILKS = [
  { name: 'PLS-A', ilk: ILK, clip: CONTRACTS.CLIPPER_PLS_A, gem: 'PLS' },
  { name: 'WPLS-A', ilk: ILK_WPLS, clip: CONTRACTS.CLIPPER_WPLS_A, gem: 'WPLS' }
];

const fNum = (x, dec) => Number(ethers.formatUnits(x, dec));

// Scan an English auction house (flap/flop) for live auctions: ids 1..kicks
// whose bid struct hasn't been deleted. Tiny system — kicks is single digits.
async function scanEnglish(contract) {
  const kicks = Number(await contract.kicks());
  const out = [];
  const from = Math.max(1, kicks - 49); // never scan more than 50 ids
  for (let id = from; id <= kicks; id++) {
    const b = await contract.bids(id);
    if (b.guy === ethers.ZeroAddress) continue; // dealt/yanked
    out.push({
      id,
      bid: b.bid,       // flap: TKR wad | flop: JDAI rad (fixed)
      lot: b.lot,       // flap: JDAI rad (fixed) | flop: TKR wad
      guy: b.guy,
      tic: Number(b.tic),
      end: Number(b.end)
    });
  }
  return { kicks, auctions: out };
}

export const useAuctions = (account) => {
  const [data, setData] = useState({ isLoading: true, error: null });

  const fetchAll = useCallback(async () => {
    try {
      const provider = new ethers.FallbackProvider(
        RPC_URLS.map((url) => new ethers.JsonRpcProvider(url))
      );
      const vat = new ethers.Contract(CONTRACTS.VAT, ABIS.VAT, provider);
      const dog = new ethers.Contract(CONTRACTS.DOG, ABIS.DOG, provider);
      const vow = new ethers.Contract(CONTRACTS.VOW, ABIS.VOW_AUCTION, provider);
      const flap = new ethers.Contract(CONTRACTS.FLAPPER, ABIS.FLAPPER, provider);
      const flop = new ethers.Contract(CONTRACTS.FLOPPER, ABIS.FLOPPER, provider);
      const tkr = new ethers.Contract(CONTRACTS.TKR, ABIS.TKR, provider);

      const [Hole, vowDai, vowSin, Sin, Ash, bump, hump, sump, dump, tkrSupply] =
        await Promise.all([
          dog.Hole(), vat.dai(CONTRACTS.VOW), vat.sin(CONTRACTS.VOW),
          vow.Sin(), vow.Ash(), vow.bump(), vow.hump(), vow.sump(), vow.dump(),
          tkr.totalSupply()
        ]);

      // Collateral (Dutch) auctions per ilk
      const collateral = [];
      for (const c of CLIP_ILKS) {
        const clip = new ethers.Contract(c.clip, ABIS.CLIPPER, provider);
        const [ids, dIlk] = await Promise.all([clip.list(), dog.ilks(c.ilk)]);
        const auctions = [];
        for (const id of ids) {
          const [sale, status] = await Promise.all([clip.sales(id), clip.getStatus(id)]);
          auctions.push({
            id: Number(id),
            usr: sale.usr,
            tic: Number(sale.tic),
            needsRedo: status.needsRedo,
            priceRay: status.price,                      // JDAI per gem [ray]
            price: fNum(status.price, 27),
            lot: fNum(status.lot, 18),                   // gem remaining
            tab: fNum(status.tab, 45),                   // JDAI still owed
            lotRaw: status.lot,
            tabRaw: status.tab
          });
        }
        collateral.push({ ...c, hole: fNum(dIlk.hole, 45), dirt: fNum(dIlk.dirt, 45), auctions });
      }

      // Surplus (flap) and debt (flop) English auctions
      const [flapScan, flopScan, flapLive, flopLive] = await Promise.all([
        scanEnglish(flap), scanEnglish(flop), flap.live(), flop.live()
      ]);

      const unqueued = vowSin - Sin - Ash;
      // Reachability credits the kick buttons' auto-heal prerequisite:
      //  flap: needs unqueued == 0 at call — but dai >= sin + bump + hump implies
      //        dai >= unqueued, so the auto-heal can always zero it first
      //  flop: needs dai == 0 at call — after auto-healing all surplus into the
      //        bad debt, what remains must still exceed sump
      const flapReachable = vowDai >= vowSin + bump + hump;
      const flopReachable = unqueued >= vowDai && unqueued - vowDai >= sump;

      // Connected-account bidding state
      let user = null;
      if (account) {
        const jdai = new ethers.Contract(CONTRACTS.JDAI, ABIS.JDAI, provider);
        const [vatDai, walletJdai, tkrBal, tkrAllow, canPls, canWpls, canFlop] =
          await Promise.all([
            vat.dai(account),
            jdai.balanceOf(account),
            tkr.balanceOf(account),
            tkr.allowance(account, CONTRACTS.FLAPPER),
            vat.can(account, CONTRACTS.CLIPPER_PLS_A),
            vat.can(account, CONTRACTS.CLIPPER_WPLS_A),
            vat.can(account, CONTRACTS.FLOPPER)
          ]);
        user = {
          vatDai, vatDaiNum: fNum(vatDai, 45),
          walletJdai, walletJdaiNum: fNum(walletJdai, 18),
          tkrBal, tkrBalNum: fNum(tkrBal, 18),
          flapApproved: tkrAllow > 10n ** 30n,
          hoped: { 'PLS-A': canPls === 1n, 'WPLS-A': canWpls === 1n, flop: canFlop === 1n }
        };
      }

      setData({
        isLoading: false, error: null,
        armed: Hole > 0n,
        user,
        collateral,
        flap: { live: flapLive === 1n, reachable: flapReachable, ...flapScan },
        flop: { live: flopLive === 1n, reachable: flopReachable, ...flopScan },
        vow: {
          surplus: fNum(vowDai, 45), sinTotal: fNum(vowSin, 45),
          queued: fNum(Sin, 45), ash: fNum(Ash, 45), unqueued: fNum(unqueued, 45),
          surplusRaw: vowDai, unqueuedRaw: unqueued,
          bump: fNum(bump, 45), hump: fNum(hump, 45),
          sump: fNum(sump, 45), dump: fNum(dump, 18),
          humpCold: hump >= 10n ** 6n * RAD // parked unreachably high = deliberately dormant
        },
        tkrSupply: fNum(tkrSupply, 18)
      });
    } catch (e) {
      setData((d) => ({ ...d, isLoading: false, error: e?.shortMessage || e?.message || 'read failed' }));
    }
  }, [account]);

  useEffect(() => {
    fetchAll();
    const t = setInterval(fetchAll, 30000);
    return () => clearInterval(t);
  }, [fetchAll]);

  return { ...data, refresh: fetchAll };
};

export { RAY, RAD, WAD };
